import React, { useState } from "react";
import PropTypes from "prop-types";
import Box from "@mui/material/Box";
import Collapse from "@mui/material/Collapse";
import IconButton from "@mui/material/IconButton";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import ListItemIcon from "@mui/material/ListItemIcon";
import ListItemText from "@mui/material/ListItemText";
import Paper from "@mui/material/Paper";
import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import Typography from "@mui/material/Typography";
import Divider from "@mui/material/Divider";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import "../Table.css";

const CollapsibleRow = ({
  row,
  columnsdata,
  detailColumnsdata,
  detailRowKey,
  menuActions,
  liftRowActions,
}) => {
  const [open, setOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);

  const menuOpen = Boolean(anchorEl);
  const detailRows = Array.isArray(row[detailRowKey]) ? row[detailRowKey] : [];
  const rowMenuActions =
    typeof menuActions === "function" ? menuActions(row) : menuActions;

  const handleMenuClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  return (
    <>
      <TableRow sx={{ "& > *": { borderBottom: "unset" } }}>
        <TableCell>
          <IconButton
            aria-label="expand row"
            size="small"
            onClick={() => setOpen(!open)}
          >
            {open ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
          </IconButton>
        </TableCell>
        <TableCell>
          {rowMenuActions && rowMenuActions.length > 0 ? (
            <>
              <IconButton
                size="small"
                onClick={handleMenuClick}
                aria-controls={menuOpen ? "row-menu" : undefined}
                aria-haspopup="true"
                aria-expanded={menuOpen ? "true" : undefined}
              >
                <MoreVertIcon />
              </IconButton>
              <Menu
                id="row-menu"
                anchorEl={anchorEl}
                open={menuOpen}
                onClose={handleMenuClose}
              >
                {rowMenuActions.map((action, index) => {
                  const actionLabel =
                    typeof action.label === "function"
                      ? action.label(row)
                      : action.label;
                  const actionIcon =
                    typeof action.icon === "function"
                      ? action.icon(row)
                      : action.icon;
                  const actionVisible =
                    typeof action.show === "function"
                      ? action.show(row)
                      : action.show !== false;

                  if (!actionVisible) {
                    return null;
                  }

                  return (
                  <MenuItem
                    key={index}
                    onClick={() => {
                      handleMenuClose();
                      action.onClick && action.onClick(row);
                    }}
                  >
                    <ListItemIcon sx={{ color: action.color }}>
                      {actionIcon}
                    </ListItemIcon>
                    <ListItemText sx={{ color: action.color }}>
                      {actionLabel}
                    </ListItemText>
                  </MenuItem>
                  );
                })}
              </Menu>
            </>
          ) : null}
        </TableCell>
        {columnsdata.map((col) => (
          <TableCell key={col.accessorKey} align={col.align || "left"}>
            {row[col.accessorKey]}
          </TableCell>
        ))}
      </TableRow>
      <TableRow>
        <TableCell
          style={{ paddingBottom: 0, paddingTop: 0 }}
          colSpan={columnsdata.length + 2}
        >
          <Collapse in={open} timeout="auto" unmountOnExit>
            <Box sx={{ margin: 2 }}>
              <Table size="small" aria-label="lifts">
                <TableHead>
                  <TableRow sx={{ backgroundColor: "#f5f5f5" }}>
                    <TableCell />
                    {detailColumnsdata.map((col) => (
                      <TableCell
                        key={col.accessorKey}
                        align={col.align || "left"}
                      >
                        <strong>{col.header}</strong>
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {detailRows.map((detailRow, index) => (
                    <TableRow key={index}>
                      <TableCell>
                        <Box sx={{ display: "flex", gap: 0.5 }}>
                          {liftRowActions &&
                            liftRowActions.map((action, aIndex) => {
                              const actionLabel =
                                typeof action.label === "function"
                                  ? action.label(detailRow, row)
                                  : action.label;
                              const actionIcon =
                                typeof action.icon === "function"
                                  ? action.icon(detailRow, row)
                                  : action.icon;
                              const actionVisible =
                                typeof action.show === "function"
                                  ? action.show(detailRow, row)
                                  : action.show !== false;

                              if (!actionVisible) {
                                return null;
                              }

                              return (
                              <IconButton
                                key={aIndex}
                                size="small"
                                onClick={() =>
                                  action.onClick && action.onClick(row, detailRow)
                                }
                                sx={{ color: action.color || "#666" }}
                                title={actionLabel}
                              >
                                {actionIcon}
                              </IconButton>
                              );
                            })}
                        </Box>
                      </TableCell>
                      {detailColumnsdata.map((col) => (
                        <TableCell
                          key={col.accessorKey}
                          align={col.align || "left"}
                        >
                          {detailRow[col.accessorKey]}
                        </TableCell>
                      ))}
                    </TableRow>
                  ))}
                  {detailRows.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={detailColumnsdata.length + 1}>
                        No details found.
                      </TableCell>
                    </TableRow>
                  ) : null}
                </TableBody>
              </Table>
            </Box>
          </Collapse>
        </TableCell>
      </TableRow>
    </>
  );
};

const MobileCard = ({
  row,
  columnsdata,
  detailColumnsdata,
  detailRowKey,
  menuActions,
  liftRowActions,
}) => {
  const [open, setOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);

  const menuOpen = Boolean(anchorEl);
  const detailRows = Array.isArray(row[detailRowKey]) ? row[detailRowKey] : [];
  const rowMenuActions =
    typeof menuActions === "function" ? menuActions(row) : menuActions;

  const handleMenuClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  return (
    <Card variant="outlined" sx={{ mb: 2, backgroundColor: "#F1F2FF" }}>
      <CardContent>
        <Grid container alignItems="center" justifyContent="space-between">
          <Typography variant="subtitle2">
            {row[columnsdata[0]?.accessorKey]}
          </Typography>
          <Box>
            {rowMenuActions && rowMenuActions.length > 0 ? (
              <>
                <IconButton size="small" onClick={handleMenuClick}>
                  <MoreVertIcon />
                </IconButton>
                <Menu
                  id="mobile-row-menu"
                  anchorEl={anchorEl}
                  open={menuOpen}
                  onClose={handleMenuClose}
                >
                  {rowMenuActions.map((action, index) => {
                    const actionLabel =
                      typeof action.label === "function"
                        ? action.label(row)
                        : action.label;
                    const actionIcon =
                      typeof action.icon === "function"
                        ? action.icon(row)
                        : action.icon;
                    const actionVisible =
                      typeof action.show === "function"
                        ? action.show(row)
                        : action.show !== false;

                    if (!actionVisible) {
                      return null;
                    }

                    return (
                    <MenuItem
                      key={index}
                      onClick={() => {
                        handleMenuClose();
                        action.onClick && action.onClick(row);
                      }}
                    >
                      <ListItemIcon sx={{ color: action.color }}>
                        {actionIcon}
                      </ListItemIcon>
                      <ListItemText sx={{ color: action.color }}>
                        {actionLabel}
                      </ListItemText>
                    </MenuItem>
                    );
                  })}
                </Menu>
              </>
            ) : null}
            <IconButton size="small" onClick={() => setOpen(!open)}>
              {open ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
            </IconButton>
          </Box>
        </Grid>
        <Divider sx={{ my: 1 }} />
        {columnsdata.map((col) => (
          <Typography key={col.accessorKey} variant="body2">
            {col.header}: <b>{row[col.accessorKey]}</b>
          </Typography>
        ))}
        <Collapse in={open} timeout="auto" unmountOnExit>
          <Divider sx={{ my: 1 }} />
          {detailRows.map((detailRow, index) => (
            <Box key={index} sx={{ mb: 1 }}>
              {detailColumnsdata.map((col) => (
                <Typography key={col.accessorKey} variant="body2">
                  {col.header}: <b>{detailRow[col.accessorKey]}</b>
                </Typography>
              ))}
              {liftRowActions && liftRowActions.length > 0 ? (
                <Box sx={{ display: "flex", gap: 0.5, mt: 1 }}>
                  {liftRowActions.map((action, aIndex) => {
                    const actionLabel =
                      typeof action.label === "function"
                        ? action.label(detailRow, row)
                        : action.label;
                    const actionIcon =
                      typeof action.icon === "function"
                        ? action.icon(detailRow, row)
                        : action.icon;
                    const actionVisible =
                      typeof action.show === "function"
                        ? action.show(detailRow, row)
                        : action.show !== false;

                    if (!actionVisible) {
                      return null;
                    }

                    return (
                    <IconButton
                      key={aIndex}
                      size="small"
                      onClick={() =>
                        action.onClick && action.onClick(row, detailRow)
                      }
                      sx={{ color: action.color || "#666" }}
                      title={actionLabel}
                    >
                      {actionIcon}
                    </IconButton>
                    );
                  })}
                </Box>
              ) : null}
              <Divider sx={{ my: 1 }} />
            </Box>
          ))}
          {detailRows.length === 0 ? (
            <Typography variant="body2">No details found.</Typography>
          ) : null}
        </Collapse>
      </CardContent>
    </Card>
  );
};

const CollapsibleTable = ({
  columnsdata,
  rowsdata,
  detailColumnsdata,
  detailRowKey = "lifts",
  menuActions,
  liftRowActions,
}) => {
  return (
    <Grid container justifyContent="center">
      <Grid item className="DesktopTable">
        <TableContainer component={Paper}>
          <Table aria-label="collapsible table">
            <TableHead>
              <TableRow sx={{ backgroundColor: "#e0e0e0" }}>
                <TableCell />
                <TableCell />
                {columnsdata.map((col) => (
                  <TableCell key={col.accessorKey} align={col.align || "left"}>
                    <strong>{col.header}</strong>
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {rowsdata.map((row, index) => (
                <CollapsibleRow
                  key={row.documentNo || index}
                  row={row}
                  columnsdata={columnsdata}
                  detailColumnsdata={detailColumnsdata}
                  detailRowKey={detailRowKey}
                  menuActions={menuActions}
                  liftRowActions={liftRowActions}
                />
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Grid>

      <Grid item className="MobileTable">
        {rowsdata.map((row, index) => (
          <MobileCard
            key={row.documentNo || index}
            row={row}
            columnsdata={columnsdata}
            detailColumnsdata={detailColumnsdata}
            detailRowKey={detailRowKey}
            menuActions={menuActions}
            liftRowActions={liftRowActions}
          />
        ))}
      </Grid>
    </Grid>
  );
};

CollapsibleTable.propTypes = {
  columnsdata: PropTypes.array.isRequired,
  rowsdata: PropTypes.array.isRequired,
  detailColumnsdata: PropTypes.array.isRequired,
  detailRowKey: PropTypes.string,
  menuActions: PropTypes.oneOfType([PropTypes.array, PropTypes.func]),
  liftRowActions: PropTypes.oneOfType([PropTypes.array, PropTypes.func]),
};

export default CollapsibleTable;
